import Image from 'next/image';
import { memo, useEffect, useRef, useState } from 'react';

import { isAudio, isImage } from '@/components/BatchUpload/common/utils';
import { getVideoCoverUrl } from '@/utils/fileUtil';
import TaskQueue from '@/utils/taskQueue';

export type Item = {
  createTime: number;
  duration: number;
  extendInfo: { duration: number };
  fileUrl: string;
  id: number;
  // value=0 视频翻译 1视频换脸 2字幕擦除 3唇音驱动 4克隆数字人 5脚本编辑
  taskType: 0 | 1 | 2 | 3 | 4 | 5;
  title: string;
  // 1-文件夹，2-图片，3-视频，4-字体,5-音频,6- 音色音频 ，7- zip 文件,8-文本
  type: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  previewUrl: string;
  cover?: string;
};
const millisecondsToHMS = (milliseconds: number) => {
  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  const ss = Math.floor(milliseconds % 1000);
  const padZero = (value: number) => (value < 10 ? `0${value}` : value);
  return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
};

function formatTimestamp(t: number) {
  const date = new Date(t);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份从0开始，所以要加1
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}
interface VideoItemProps {
  item: Item;
  onSelect: (item: Item, status: boolean) => void;
}

const CheckBox: React.FC<{
  selectd: boolean;
}> = (props) => {
  return (
    <div className="w-[16px] h-[16px] rounded-[2px] border-[1px] border-color-border cursor-pointer flex flex-row items-center justify-center">
      {props.selectd && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="9"
          viewBox="0 0 11 9"
          fill="none"
        >
          <path
            d="M1 4.77247L3.86347 7.63594L10 1.5"
            stroke="#5661F1"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

const ImageContainer = memo(
  ({ coverUrl }: { coverUrl: string }) => {
    return (
      <Image
        loading="lazy"
        priority={false}
        unoptimized={true}
        className="transition-all duration-300 ease-in-out transform 
      opacity-0 scale-95 
      group-hover:opacity-100 group-hover:scale-100"
        style={{
          userSelect: 'none',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
        onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const imageElement = e.target as HTMLImageElement;
          imageElement.classList.remove('opacity-0', 'scale-95');
          imageElement.classList.add('opacity-100', 'scale-100');
        }}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const imageElement = e.target as HTMLImageElement;
          imageElement.src = '/images/default-cover-video.png';
          imageElement.classList.remove('opacity-0', 'scale-95');
          imageElement.classList.add('opacity-100', 'scale-100');
        }}
        fill={true}
        src={coverUrl}
        alt=""
      />
    );
  },
  (prevProps, nextProps) => prevProps.coverUrl === nextProps.coverUrl,
);

export const VideoItem: React.FC<VideoItemProps> = (props) => {
  const { item, onSelect } = props;

  const { title, createTime } = item;
  const [selectd, setSelected] = useState(false);
  const [coverUrl, setCoverUrl] = useState<string>('/images/default-cover-video.png');
  const itemRef = useRef<HTMLDivElement>(null);
  const taskQueueRef = useRef<TaskQueue>();

  useEffect(() => {
    if (!taskQueueRef.current) {
      taskQueueRef.current = new TaskQueue({ concurrency: 1 });
    }

    const observerDom = itemRef.current;
    const getCover = async () => {
      const suffix = '.' + item.fileUrl.split('.').pop();
      if (isAudio(suffix)) {
        setCoverUrl(item.fileUrl);
      } else if (isImage(suffix)) {
        setCoverUrl(item.cover || item.fileUrl);
      } else {
        const cover = await getVideoCoverUrl(item.cover!, item.fileUrl);
        setCoverUrl(cover);
      }
    };
    if (!observerDom) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          taskQueueRef.current?.push(getCover);
          observer.unobserve(entry.target);
        }
      });
    });
    observer.observe(observerDom);
    return () => observer.disconnect();
  }, [item.cover, item.fileUrl]);

  return (
    <div
      ref={itemRef}
      className="flex-none h-[60px] px-[12px] flex flex-row gap-[16px] items-center video-tool-item-observe"
      onClick={() => {
        const status = !selectd;
        setSelected(status);
        onSelect(item, status);
      }}
    >
      <CheckBox selectd={selectd} />
      <div className="flex-1 flex flex-row justify-between">
        <div className="flex flex-row gap-[10px]">
          <div className="w-[46px] h-[60px] rounded-[4px] overflow-hidden select-none relative">
            <ImageContainer coverUrl={coverUrl} />
          </div>
          <div className="w-[260px] flex flex-col gap-[4px] justify-center">
            <div className="h-[24px] flex flex-row items-center justify-between gap-[6px]">
              <div className="flex-1 overflow-hidden text-ellipsis line-clamp-1 h-full text-[16px] text-[#000] font-[400] leading-[24px]">
                {title}
              </div>
            </div>
            {item.type !== 2 && (
              <div className="h-[15px] text-[12px] text-[#000] font-[400]">
                {millisecondsToHMS((item.duration || item.extendInfo.duration) ?? 0)}
              </div>
            )}
          </div>
        </div>
        <div className="w-[130px] text-right flex flex-col justify-center">
          <span className="inline-block h-[24px] text-[12px] text-[#000] font-[400] leading-[24px]">
            {formatTimestamp(createTime)}
          </span>
        </div>
      </div>
    </div>
  );
};
